// The tutor's voice: text-to-speech via the Web Speech API
// (`window.speechSynthesis`).
//
// This is the browser counterpart of the Android build's `TextToSpeech` half of
// `speech/SpeechController.kt`, and exposes the same surface (speak / stop /
// rate), so the store consumes it unchanged.
//
// The Web Speech API has three quirks worth knowing about, all handled here:
//   1. getVoices() is often empty on first call and fills in asynchronously,
//      signalled by the `voiceschanged` event.
//   2. Chrome silently stops speaking after ~15 seconds; a periodic
//      pause()/resume() ping keeps long utterances alive.
//   3. iOS Safari refuses to speak until speech has been started once from a
//      real user gesture — see `primeSpeech()`.

export function isSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * Removes emoji and the ✏️/💡 markers so the engine doesn't read them aloud —
 * the same job as `stripDecoration` in the Kotlin controller.
 */
export function stripDecoration(text: string): string {
  return text
    // Pictographs, plus the zero-width joiner and variation selector that glue
    // multi-codepoint emoji together.
    .replace(/[\p{Extended_Pictographic}\u200D\uFE0F]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Resolve the browser's voice list, waiting for `voiceschanged` when the first
 * synchronous call comes back empty. Resolves with whatever is available after
 * `timeoutMs` rather than hanging.
 */
function getVoicesAsync(timeoutMs = 2000): Promise<SpeechSynthesisVoice[]> {
  return new Promise((resolve) => {
    if (!isSupported()) return resolve([]);

    const immediate = window.speechSynthesis.getVoices();
    if (immediate.length) return resolve(immediate);

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      window.speechSynthesis.removeEventListener('voiceschanged', finish);
      clearTimeout(timer);
      resolve(window.speechSynthesis.getVoices());
    };

    window.speechSynthesis.addEventListener('voiceschanged', finish);
    const timer = setTimeout(finish, timeoutMs);
  });
}

let preferredVoice: SpeechSynthesisVoice | null = null;

/**
 * The tutor is meant to sound female — but the Web Speech API exposes no
 * gender field, only names. This is the curated list of female voice names the
 * major platforms ship: Windows/SAPI and Edge's neural voices, macOS/iOS, and
 * Chrome's own bundled voice.
 *
 * The Android build got this for free: it only set `Locale.US` and the default
 * Android TTS voice is female. On Windows the default is *Microsoft David*, a
 * male voice, which is why picking "the first en-US voice" was wrong here.
 */
const FEMALE_VOICE_NAMES: ReadonlySet<string> = new Set([
  // Windows / Edge
  'zira', 'aria', 'jenny', 'michelle', 'ana', 'eva', 'hazel', 'susan', 'sonia',
  'libby', 'clara', 'natasha', 'emily',
  // macOS / iOS
  'samantha', 'ava', 'allison', 'karen', 'moira', 'tessa', 'fiona', 'victoria',
  'serena', 'nicky', 'zoe', 'kathy', 'princess',
  // generic labels some engines use
  'female', 'woman',
]);

/** True when any whole word of the voice's name is a known female name. */
function soundsFemale(voice: SpeechSynthesisVoice): boolean {
  const words = (voice.name || '').toLowerCase().split(/[^a-z]+/);
  return words.some((word) => FEMALE_VOICE_NAMES.has(word));
}

/**
 * Picks one voice up front and keeps using it, so the tutor sounds like the
 * same person for the whole conversation: a female en-US voice when the browser
 * has one, then a female English voice, then any en-US voice rather than none.
 */
export async function loadVoice(): Promise<boolean> {
  if (preferredVoice) return true;
  try {
    const all = await getVoicesAsync();
    const english = all.filter((v) => (v.lang || '').toLowerCase().startsWith('en'));
    const isUs = (v: SpeechSynthesisVoice) =>
      (v.lang || '').toLowerCase().replace('_', '-') === 'en-us';
    const us = english.filter(isUs);

    preferredVoice =
      us.find(soundsFemale) ??
      english.find(soundsFemale) ??
      // Chrome's own bundled "Google US English" reads female.
      us.find((v) => /google us english/i.test(v.name)) ??
      us[0] ??
      english[0] ??
      null;
    return preferredVoice !== null;
  } catch (e) {
    console.warn('[tts] voice enumeration failed', e);
    return false;
  }
}

/** The voice the tutor speaks with, for display. Null until `loadVoice` runs. */
export function currentVoiceName(): string | null {
  return preferredVoice?.name ?? null;
}

// Chrome's ~15s watchdog workaround.
let keepAlive: ReturnType<typeof setInterval> | null = null;
function startKeepAlive() {
  stopKeepAlive();
  keepAlive = setInterval(() => {
    if (!window.speechSynthesis.speaking) return stopKeepAlive();
    window.speechSynthesis.pause();
    window.speechSynthesis.resume();
  }, 10_000);
}
function stopKeepAlive() {
  if (keepAlive !== null) {
    clearInterval(keepAlive);
    keepAlive = null;
  }
}

interface SpeakOpts {
  /** 1.0 is normal; the Android build used 0.95 for chat and 0.85 for drills. */
  rate?: number;
  onStart?: () => void;
  onDone?: () => void;
}

// Tracks the utterance we most recently asked for, so late events from a
// cancelled utterance can be ignored.
let currentUtterance: SpeechSynthesisUtterance | null = null;

export function speak(text: string, opts: SpeakOpts = {}): void {
  if (!isSupported()) return;
  const clean = stripDecoration(text);
  if (!clean) return;

  const synth = window.speechSynthesis;
  // Any pending utterance is ours and is now obsolete.
  synth.cancel();

  const utter = new SpeechSynthesisUtterance(clean);
  utter.lang = 'en-US';
  utter.rate = opts.rate ?? 0.95;
  utter.pitch = 1.0;
  if (preferredVoice) {
    utter.voice = preferredVoice;
    utter.lang = preferredVoice.lang;
  }

  const isCurrent = () => currentUtterance === utter;

  utter.onstart = () => {
    if (!isCurrent()) return;
    startKeepAlive();
    opts.onStart?.();
  };
  utter.onend = () => {
    if (!isCurrent()) return;
    stopKeepAlive();
    currentUtterance = null;
    opts.onDone?.();
  };
  utter.onerror = (e) => {
    stopKeepAlive();
    if (!isCurrent()) return;
    currentUtterance = null;
    // `interrupted` / `canceled` are what cancel() produces — that is a stop,
    // not a failure.
    if (e.error !== 'interrupted' && e.error !== 'canceled') {
      console.warn('[tts] utterance error', e.error);
    }
    opts.onDone?.();
  };

  currentUtterance = utter;
  // Chrome can drop an utterance queued in the same tick as cancel().
  setTimeout(() => {
    if (isCurrent()) synth.speak(utter);
  }, 0);
}

/**
 * True while the tutor is actually talking. The hands-free microphone stays
 * open during replies, so without this the recogniser transcribes the tutor's
 * own voice out of the speakers and posts it back as the learner's next
 * message — a conversation the app holds with itself.
 */
export function isCurrentlySpeaking(): boolean {
  return isSupported() && window.speechSynthesis.speaking;
}

export function stop(): void {
  if (!isSupported()) return;
  stopKeepAlive();
  currentUtterance = null;
  window.speechSynthesis.cancel();
}

/**
 * iOS Safari (and some mobile Chrome builds) only allow speech once it has been
 * initiated from a user gesture. Speaking a single silent utterance inside the
 * first real interaction unlocks the engine for the whole session.
 */
let primed = false;
export function primeSpeech(): void {
  if (primed || !isSupported()) return;
  primed = true;
  try {
    const utter = new SpeechSynthesisUtterance('');
    utter.volume = 0;
    window.speechSynthesis.speak(utter);
  } catch {
    // best-effort only
  }
}
